// BlinkBooks app — chrome (rail, header, tab bar) + routing, mounted at the root.
import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { MessageSquareHeart } from "lucide-react";
import "./blink.css";
import posthog from "@/lib/posthog";
import { Icon } from "@/components/Icon";
import { Avatar } from "@/components/primitives";
import { FeedbackModal } from "@/components/FeedbackModal";
import { useAuth } from "@/contexts/AuthContext";
import { signOutUser } from "@/services/firebaseAuth.service";
import { NotImplementedProvider, useNotImplemented } from "@/hooks/use-not-implemented";
import { DATA } from "@/data";
import { HomePage } from "@/pages/Home";
import { DashboardScreen, SkillsScreen, LibraryScreen } from "@/pages/agents/AIAgents";
import { ApprovalsScreen } from "@/pages/agents/Approvals";
import { AskAIOverlay, AskAIScreen } from "@/pages/askai/AskAI";
import { FinOverview, FinKPIs, FinCash } from "@/pages/finance/Finance";
import { FinTxns, FinReports } from "@/pages/finance/Transactions";
import { SettingsHub } from "@/pages/settings/Settings";

interface NavSection {
  id: string;
  label: string;
  icon: string;
  badge?: number;
  tabs: { id: string; label: string; badge?: number }[];
}

const PENDING_APPROVALS = DATA.approvals.filter((a) => a.status === "pending").length;

const NAV: NavSection[] = [
  { id: "home", label: "Home", icon: "home", tabs: [] },
  {
    id: "aiagents", label: "AI Agents", icon: "agents", badge: PENDING_APPROVALS, tabs: [
      { id: "dashboard", label: "Dashboard" }, { id: "approvals", label: "Approvals", badge: PENDING_APPROVALS },
      { id: "skills", label: "Skills" }, { id: "library", label: "Agent Library" }, { id: "askai", label: "Ask AI" },
    ],
  },
  {
    id: "finance", label: "Finance", icon: "finance", tabs: [
      { id: "overview", label: "Performance" }, { id: "kpis", label: "KPIs" },
      { id: "cash", label: "Cash & Forecast" }, { id: "transactions", label: "Transactions" },
      { id: "reports", label: "Reports" },
    ],
  },
  { id: "settings", label: "Settings", icon: "gear", tabs: [] },
];

const VIEWS: Record<string, (p: { onNav: (sec: string, tab?: string) => void }) => JSX.Element> = {
  "home/": ({ onNav }) => <HomePage onNav={onNav} />,
  "aiagents/dashboard": () => <DashboardScreen />,
  "aiagents/approvals": () => <ApprovalsScreen />,
  "aiagents/skills": () => <SkillsScreen />,
  "aiagents/library": () => <LibraryScreen />,
  "aiagents/askai": () => <AskAIScreen />,
  "finance/overview": () => <FinOverview />,
  "finance/kpis": () => <FinKPIs />,
  "finance/cash": () => <FinCash />,
  "finance/transactions": () => <FinTxns />,
  "finance/reports": () => <FinReports />,
  "settings/": () => <SettingsHub />,
};

/* ── Icon rail ────────────────────────────────────────────────────── */
const Rail = ({ section, onNav }: { section: string; onNav: (id: string) => void }) => {
  const RailButton = ({ s }: { s: NavSection }) => {
    const on = s.id === section;
    return (
      <button title={s.label} onClick={() => onNav(s.id)} style={{
        width: 48, height: 50, border: "none", cursor: "pointer", borderRadius: 12,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, position: "relative",
        background: on ? "rgba(255,255,255,.12)" : "transparent", color: on ? "#fff" : "rgba(255,255,255,.5)", transition: "all .13s",
      }}>
        <Icon name={s.icon} size={20} stroke={on ? "#fff" : "rgba(255,255,255,.55)"} />
        <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: ".01em" }}>{s.label}</span>
        {on && <span style={{ position: "absolute", left: 0, top: 12, bottom: 12, width: 3, borderRadius: 3, background: "#FAF0E3" }} />}
        {s.badge && <span style={{ position: "absolute", top: 5, right: 8, minWidth: 15, height: 15, padding: "0 3px", fontSize: 9, fontWeight: 700, background: "#DC2626", color: "#fff", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #075869" }}>{s.badge}</span>}
      </button>
    );
  };
  return (
    <div style={{ width: "var(--rail-w)", background: "#075869", display: "flex", flexDirection: "column", alignItems: "center", padding: "14px 0 12px", flexShrink: 0 }}>
      <div style={{ marginBottom: 18, width: 40, height: 40, borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon name="logo-white" size={32} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, width: "100%", alignItems: "center" }}>
        {NAV.filter((s) => s.id !== "settings").map((s) => <RailButton key={s.id} s={s} />)}
      </div>
      <RailButton s={NAV.find((n) => n.id === "settings")!} />
      <div style={{ marginTop: 12 }}><Avatar name="SR" size={32} ring /></div>
    </div>
  );
};

/* ── Header panel shell (right-aligned popover) ─────────────────── */
const HeaderPanel = ({ open, onClose, width = 380, children }: {
  open: boolean; onClose: () => void; width?: number; children: React.ReactNode;
}) => open ? <>
  <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
  <div className="fade-up" style={{ position: "absolute", top: 44, right: 0, width, maxHeight: "72vh", background: "#fff", border: "1px solid var(--line)", borderRadius: 14, boxShadow: "var(--sh-pop)", zIndex: 41, overflow: "hidden", overflowY: "auto" }}>{children}</div>
</> : null;

const ActIcon = ({ kind }: { kind: string }) => {
  const m = ({
    alert: ["alert", "var(--warn)"], check: ["check-circle", "var(--pos)"], doc: ["doc", "var(--brand)"],
    comment: ["comment", "var(--info)"], refresh: ["refresh", "var(--ink-3)"], user: ["user", "var(--violet)"],
  } as Record<string, [string, string]>)[kind] || ["activity", "var(--ink-3)"];
  return (
    <span style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: m[1] + "14" }}>
      <Icon name={m[0]} size={15} stroke={m[1]} />
    </span>
  );
};

const KIND_STYLE: Record<string, string> = { Mention: "b-info", Approval: "b-brand", Review: "b-violet", Reply: "b-mute" };

/* Notifications — mentions, comments, approvals, AI alerts (personal) */
const Notifications = () => {
  const [open, setOpen] = useState(false);
  const notify = useNotImplemented();
  const unread = DATA.inbox.filter((n) => (n as { unread?: boolean }).unread).length;
  return (
    <div style={{ position: "relative" }}>
      <button className="btn btn-sec btn-icon" onClick={() => setOpen((o) => { if (!o) posthog.capture("notifications_opened", { unread }); return !o; })} title="Notifications" style={{ position: "relative" }}>
        <Icon name="bell" size={17} stroke="var(--ink-2)" />
        {unread > 0 && <span style={{ position: "absolute", top: -4, right: -4, minWidth: 16, height: 16, fontSize: 10, fontWeight: 700, background: "var(--neg)", color: "#fff", border: "2px solid #fff", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>{unread}</span>}
      </button>
      <HeaderPanel open={open} onClose={() => setOpen(false)}>
        <div style={{ padding: "13px 16px", borderBottom: "1px solid var(--line-2)", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff" }}>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>Notifications</span>
          <span className="link" style={{ fontSize: 12, cursor: "pointer" }} onClick={() => notify("Mark all read")}>Mark all read</span>
        </div>
        {DATA.inbox.map((n, i) => {
          const unreadRow = (n as { unread?: boolean }).unread;
          return (
            <div key={i} onClick={() => notify("Notification detail")} style={{ display: "flex", gap: 11, padding: "12px 16px", borderBottom: i < DATA.inbox.length - 1 ? "1px solid var(--line-2)" : "none", cursor: "pointer", background: unreadRow ? "#fff" : "var(--surface-2)" }}>
              <Avatar name={n.av} size={32} sq />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                  <span className={"badge " + KIND_STYLE[n.kind]}>{n.kind}</span>
                  {unreadRow && <span style={{ width: 7, height: 7, borderRadius: 50, background: "var(--brand)" }} />}
                  <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--ink-4)" }}>{n.time}</span>
                </div>
                <div style={{ fontSize: 12.5, fontWeight: unreadRow ? 600 : 500, lineHeight: 1.4 }}>{n.title}</div>
                <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n.ctx}</div>
              </div>
            </div>
          );
        })}
      </HeaderPanel>
    </div>
  );
};

/* Activity — global timeline of team + AI + financial events */
const ActivityMenu = () => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button className="btn btn-sec btn-icon" onClick={() => setOpen((o) => { if (!o) posthog.capture("activity_panel_opened"); return !o; })} title="Activity">
        <Icon name="activity" size={17} stroke="var(--ink-2)" />
      </button>
      <HeaderPanel open={open} onClose={() => setOpen(false)} width={400}>
        <div style={{ padding: "13px 16px", borderBottom: "1px solid var(--line-2)", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff" }}>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>Activity</span>
          <span style={{ fontSize: 11.5, color: "var(--ink-4)" }}>Team · AI · Financial</span>
        </div>
        {DATA.activity.map((a, i) => (
          <div key={i} style={{ display: "flex", gap: 11, padding: "12px 16px", borderBottom: i < DATA.activity.length - 1 ? "1px solid var(--line-2)" : "none", alignItems: "flex-start" }}>
            <ActIcon kind={a.kind} />
            <div style={{ flex: 1, minWidth: 0, paddingTop: 1 }}>
              <div style={{ fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.45 }}>
                <b style={{ color: "var(--ink)" }}>{a.who}</b>
                {a.type === "agent" && <span className="badge b-brand" style={{ margin: "0 5px", transform: "translateY(-1px)" }}>Agent</span>}
                {" "}{a.action}{" "}
                <span style={{ fontWeight: 500, color: "var(--ink)" }}>{a.target}</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 2 }}>{a.time} ago</div>
            </div>
          </div>
        ))}
      </HeaderPanel>
    </div>
  );
};

/* User menu — avatar dropdown with account info + logout */
const UserMenu = () => {
  const [open, setOpen] = useState(false);
  const { profile } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    posthog.capture("logout_clicked");
    setOpen(false);
    try {
      await signOutUser();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <button onClick={() => setOpen((o) => !o)} title="Account" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", borderRadius: "50%" }}>
        <Avatar name="SR" size={32} />
      </button>
      <HeaderPanel open={open} onClose={() => setOpen(false)} width={260}>
        {profile && (
          <div style={{ padding: "13px 16px", borderBottom: "1px solid var(--line-2)" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{profile.email}</div>
            {profile.organization && <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 2 }}>{profile.organization}</div>}
          </div>
        )}
        <button
          onClick={handleLogout}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "11px 16px",
            border: "none", background: "none", cursor: "pointer", font: "inherit", fontSize: 13,
            fontWeight: 500, color: "var(--neg)", textAlign: "left",
          }}
        >
          <Icon name="logout" size={16} stroke="var(--neg)" />
          Log out
        </button>
      </HeaderPanel>
    </div>
  );
};

/* Feedback — purple icon button, opens the Apple-style feedback form */
const FeedbackButton = ({ onOpen }: { onOpen: () => void }) => (
  <button
    className="btn btn-sec btn-icon"
    title="Send Feedback"
    onClick={onOpen}
    style={{ color: "#7C3AED", borderColor: "#DDD6FE" }}
  >
    <MessageSquareHeart size={17} strokeWidth={1.7} />
  </button>
);

/* ── Shell — reads /:section/:tab from the URL ────────────────────── */
const Shell = () => {
  const params = useParams<{ section?: string; tab?: string }>();
  const navigate = useNavigate();
  const [ask, setAsk] = useState<{ open: boolean; seed: string | null }>({ open: false, seed: null });
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const section = params.section && NAV.some((n) => n.id === params.section) ? params.section : "home";
  const nav = NAV.find((n) => n.id === section)!;
  const tab = params.tab && nav.tabs.some((t) => t.id === params.tab) ? params.tab : nav.tabs[0]?.id || "";

  const openAsk = (seed?: string, source = "page_link") => {
    posthog.capture("askai_opened", { source, seeded: !!seed });
    setAsk({ open: true, seed: seed || null });
  };

  // Global ⌘K / Ctrl+K — Ask AI is reachable from every screen
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setAsk((a) => {
          if (!a.open) posthog.capture("askai_opened", { source: "keyboard_shortcut", seeded: false });
          return a.open ? { open: false, seed: null } : { open: true, seed: null };
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const goSection = (id: string) => {
    posthog.capture("nav_section_clicked", { section: id });
    const s = NAV.find((n) => n.id === id)!;
    navigate(`/${id}${s.tabs[0] ? "/" + s.tabs[0].id : ""}`);
  };
  const goNav = (sec: string, t?: string) => {
    if (sec === "ai" || sec === "askai") { openAsk(); return; }
    const s = NAV.find((n) => n.id === sec);
    if (!s) return;
    const target = t && s.tabs.some((x) => x.id === t) ? t : s.tabs[0]?.id;
    navigate(`/${sec}${target ? "/" + target : ""}`);
  };

  const key = section + "/" + tab;
  const View = VIEWS[key] || (() => <div />);
  const fullBleed = key === "aiagents/askai";

  return (
    <div className="bb" style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      {/* Demo banner */}
      <div style={{ flexShrink: 0, height: 34, background: "linear-gradient(90deg, #F5B700 0%, #FFC933 50%, #FFDA6A 100%)", borderBottom: "1px solid rgba(0,0,0,.06)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "0 16px" }}>
        <span className="badge" style={{ height: 18, fontSize: 9.5, textTransform: "uppercase", letterSpacing: ".05em", background: "#17313A", color: "#FFDA6A" }}>Demo</span>
        <span style={{ fontSize: 12, color: "rgba(23,49,58,.75)" }}>
          You're exploring a demo version of BlinkBooks with sample data.
        </span>
        <a href="https://meetings-eu1.hubspot.com/itay2" target="_blank" rel="noreferrer"
          onClick={() => posthog.capture("demo_contact_link_clicked")}
          style={{ fontSize: 12, fontWeight: 700, color: "#17313A", textDecoration: "none" }}>
          For further information — let's talk →
        </a>
      </div>
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
      <Rail section={section} onNav={goSection} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* Header */}
        <header style={{ height: 56, flexShrink: 0, borderBottom: "1px solid var(--line)", background: "var(--surface)", display: "flex", alignItems: "center", gap: 14, padding: "0 22px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ width: 26, height: 26, borderRadius: 7, background: "#05686F", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11 }}>{DATA.company.monogram}</span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{DATA.company.name}</span>
            <Icon name="chevdown" size={14} stroke="var(--ink-4)" />
          </div>
          {/* Data freshness — trust signal, always visible */}
          <span title="All numbers are drawn from the latest QuickBooks sync" style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 24, padding: "0 10px", borderRadius: 12, background: "var(--surface-2)", border: "1px solid var(--line)", fontSize: 11, color: "var(--ink-3)", whiteSpace: "nowrap" }}>
            <span className="live-dot" style={{ width: 6, height: 6, borderRadius: 50, background: "var(--pos)" }} />
            QuickBooks · synced {DATA.company.synced}
          </span>
          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <button onClick={() => openAsk(undefined, "search_bar")} style={{
              display: "flex", alignItems: "center", gap: 8, width: 380, maxWidth: "100%", height: 36, padding: "0 12px",
              borderRadius: 9, border: "1px solid var(--line)", background: "var(--surface-2)", cursor: "pointer",
              color: "var(--ink-4)", font: "inherit",
            }}>
              <Icon name="search" size={15} stroke="var(--ink-4)" />
              <span style={{ fontSize: 13, flex: 1, textAlign: "left" }}>Search or ask AI…</span>
              <span className="pill-kbd">⌘K</span>
            </button>
          </div>
          <button className="btn btn-pri" onClick={() => openAsk(undefined, "header_button")}><Icon name="spark" size={15} stroke="#fff" /> Ask AI</button>
          <FeedbackButton onOpen={() => { posthog.capture("feedback_button_clicked", { section, tab }); setFeedbackOpen(true); }} />
          <ActivityMenu />
          <Notifications />
          <UserMenu />
        </header>

        {/* Tab bar */}
        {nav.tabs.length > 0 && (
          <div style={{ height: 46, flexShrink: 0, borderBottom: "1px solid var(--line)", background: "var(--surface)", display: "flex", alignItems: "flex-end", padding: "0 22px" }}>
            <div style={{ display: "flex", gap: 2, marginBottom: -1 }}>
              {nav.tabs.map((t) => {
                const on = t.id === tab;
                return (
                  <button key={t.id} onClick={() => { posthog.capture("nav_tab_clicked", { section, tab: t.id }); navigate(`/${section}/${t.id}`); }} style={{
                    border: "none", background: "none", cursor: "pointer", font: "inherit",
                    fontSize: 13.5, fontWeight: on ? 600 : 500, color: on ? "var(--ink)" : "var(--ink-3)",
                    padding: "0 13px 13px", position: "relative", display: "flex", alignItems: "center", gap: 7,
                  }}>
                    {t.label}
                    {t.badge != null && <span style={{ minWidth: 17, height: 17, padding: "0 5px", borderRadius: 8, fontSize: 10.5, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", background: on ? "var(--brand)" : "var(--surface-3)", color: on ? "#fff" : "var(--ink-3)" }}>{t.badge}</span>}
                    {on && <span style={{ position: "absolute", left: 10, right: 10, bottom: 0, height: 2, borderRadius: 2, background: "var(--brand)" }} />}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        <main key={key} style={{ flex: 1, overflowY: fullBleed ? "hidden" : "auto", background: "var(--bg)", padding: fullBleed ? 0 : "24px 22px 40px", position: "relative" }}>
          <View onNav={goNav} />
        </main>
      </div>
      </div>
      <AskAIOverlay open={ask.open} seed={ask.seed}
        onExpand={() => navigate("/aiagents/askai")}
        onClose={() => setAsk({ open: false, seed: null })} />
      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} screen={key} defaultArea={nav.label} />
    </div>
  );
};

const BlinkApp = () => (
  <NotImplementedProvider>
    <Routes>
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path=":section" element={<Shell />} />
      <Route path=":section/:tab" element={<Shell />} />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  </NotImplementedProvider>
);

export default BlinkApp;
